import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactElement } from "react";
import type { AssetMapping } from "@lunaverse-ide/shared";
import type { AssetUriMap } from "@lunaverse-ide/shared/stage";
import { Eye, PanelRight } from "lucide-react";
import { SegmentedControl } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";
import type { AssetKind, AssetMapping as WebAssetMapping } from "../../assets/mapping.js";
import type { StyleMeta } from "../../api/client.js";
import type { InsertKind } from "../editor/block-templates.js";
import { diagnosticLineFromMessage } from "../editor/CodeEditor.js";
import { CocosStepPreview, type StudioValidationState } from "./CocosStepPreview.js";
import { buildPresentationSteps, buildStoryboardBranches, buildStoryboardFrames, buildStoryboardSections, buildStoryboardTools, defaultPresentationSelection, isStoryboardKind, repairPresentationSelection, storyboardPreviewLine } from "./presentation-model.js";
import {
  insertPresentationTemplate,
  insertPresentationCompanion,
  addPresentationChoiceOption,
  deletePresentationChoiceOption,
  deletePresentationFrame,
  movePresentationFrame,
  patchPresentationStep,
  replacePresentationStepTemplate,
  setPresentationStepConcurrent,
  type PresentationStepFields,
} from "./presentation-source.js";
import { StepFilmstrip } from "./StepFilmstrip.js";
import { StepInspector } from "./StepInspector.js";
import { StudioAssetAssistant, type StudioAssetTarget } from "./StudioAssetAssistant.js";
import "./StudioView.css";

const S = {
  panel: { zh: "工作区", en: "Studio panel" },
  preview: { zh: "预览", en: "Preview" },
  edit: { zh: "编辑", en: "Edit" },
} satisfies Record<string, Entry>;

type CompactPanel = "preview" | "edit";

export interface StudioViewProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onOpenCode: (line: number) => void;
  readonly onOpenAssets: () => void;
  readonly onOpenPlaytest: () => void;
  readonly assets?: AssetMapping;
  readonly assetMapping?: WebAssetMapping;
  readonly assetUriMap?: AssetUriMap;
  readonly protagonist?: string | null;
  readonly validation?: StudioValidationState;
  readonly readOnly?: boolean;
  readonly onSelectedLineChange?: (line: number | null) => void;
  readonly imageGenerationEnabled?: boolean;
  readonly cutoutEnabled?: boolean;
  readonly styles?: readonly StyleMeta[];
  readonly assetsBusy?: boolean;
  readonly onGenerateAsset?: (args: { kind: AssetKind; symbol: string; charId?: string; prompt: string; family?: string; referenceAssetIds?: string[] }) => Promise<void> | void;
  readonly onSuggestAssetPrompt?: (args: { kind: AssetKind; name: string; styleName?: string; styleDescription?: string; notes?: string }) => Promise<string>;
  readonly onPreviewAssetPrompt?: (args: { kind: "background" | "character" | "cg"; prompt: string; family?: string; symbol?: string; charId?: string; hasAssetReference?: boolean }) => Promise<string>;
  readonly onCutoutAsset?: (args: { assetId: string; symbol: string; charId: string }) => Promise<void> | void;
}

function initialLine(value: string): number | null {
  const all = buildPresentationSteps(value);
  return defaultPresentationSelection(buildStoryboardFrames(all));
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

function closestStepLine(
  steps: readonly ReturnType<typeof buildPresentationSteps>[number][],
  diagnostic: string,
  selectedLine: number | null,
): number | null {
  const line = diagnosticLineFromMessage(diagnostic);
  if (line == null) return selectedLine ?? steps[0]?.line ?? null;
  const exact = steps.find((step) => step.line === line);
  if (exact) return exact.line;
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (steps[index]!.line < line) return steps[index]!.line;
  }
  return steps[0]?.line ?? selectedLine;
}

function visibleTimelineLine(steps: readonly ReturnType<typeof buildPresentationSteps>[number][], selectedLine: number | null): number | null {
  if (steps.some((step) => step.line === selectedLine)) return selectedLine;
  return [...steps].reverse().find((step) => selectedLine != null && step.line < selectedLine)?.line ?? steps[0]?.line ?? null;
}

export function StudioView({
  value,
  onChange,
  onOpenCode,
  onOpenAssets,
  onOpenPlaytest,
  assets,
  assetMapping,
  assetUriMap,
  protagonist,
  validation,
  readOnly,
  onSelectedLineChange,
  imageGenerationEnabled = false,
  cutoutEnabled = false,
  styles = [],
  assetsBusy,
  onGenerateAsset,
  onSuggestAssetPrompt,
  onPreviewAssetPrompt,
  onCutoutAsset,
}: StudioViewProps): ReactElement {
  const tr = useTr();
  const allSteps = useMemo(() => buildPresentationSteps(value), [value]);
  const steps = useMemo(() => buildStoryboardFrames(allSteps), [allSteps]);
  const sections = useMemo(() => buildStoryboardSections(steps), [steps]);
  const branches = useMemo(() => buildStoryboardBranches(allSteps), [allSteps]);
  const tools = useMemo(() => buildStoryboardTools(allSteps), [allSteps]);
  const timelineTools = useMemo(() => branches.some((branch) => branch.exits.length > 0) ? tools.filter((step) => step.kind !== "gate") : tools, [branches, tools]);
  const [selectedLine, setSelectedLine] = useState<number | null>(() => initialLine(value));
  const [choiceContextLine, setChoiceContextLine] = useState<number | null>(null);
  const [compactPanel, setCompactPanel] = useState<CompactPanel>("preview");

  useEffect(() => {
    setSelectedLine((current) => repairPresentationSelection(
      allSteps,
      current,
      current == null ? defaultPresentationSelection(steps) : undefined,
    ));
  }, [allSteps]);
  useEffect(() => {
    onSelectedLineChange?.(selectedLine);
  }, [onSelectedLineChange, selectedLine]);

  const selectedStep = useMemo(
    () => allSteps.find((step) => step.line === selectedLine) ?? null,
    [allSteps, selectedLine],
  );
  const optionContents = useMemo(() => {
    if (!selectedStep || selectedStep.kind !== "option") return [];
    const start = allSteps.findIndex((step) => step.line === selectedStep.line);
    const contents = [];
    for (let index = start + 1; index < allSteps.length; index += 1) {
      const candidate = allSteps[index]!;
      if (candidate.depth <= selectedStep.depth) break;
      if (candidate.kind !== "param") contents.push(candidate);
    }
    return contents;
  }, [allSteps, selectedStep]);
  const gateContents = useMemo(() => {
    if (!selectedStep || selectedStep.kind !== "gate") return [];
    const start = allSteps.findIndex((step) => step.line === selectedStep.line);
    const contents = [];
    for (let index = start + 1; index < allSteps.length; index += 1) {
      const candidate = allSteps[index]!;
      if (candidate.depth <= selectedStep.depth) break;
      if (candidate.kind === "if") contents.push(candidate);
    }
    return contents;
  }, [allSteps, selectedStep]);
  const frameCompanions = useMemo(() => {
    if (!selectedStep || selectedStep.kind !== "bg") return [];
    const start = allSteps.findIndex((step) => step.line === selectedStep.line);
    const companions = [];
    for (let index = start + 1; index < allSteps.length; index += 1) {
      const candidate = allSteps[index]!;
      if (candidate.depth !== selectedStep.depth || !candidate.concurrent) break;
      companions.push(candidate);
    }
    return companions;
  }, [allSteps, selectedStep]);
  const assetTarget = useMemo<StudioAssetTarget | null>(() => {
    const block = selectedStep?.block;
    if (!block) return null;
    if (block.kind === "bg" && block.name) return { kind: "background", symbol: block.name };
    if (block.kind === "char" && block.pose) return { kind: "character", symbol: block.pose, charId: block.character };
    if (block.kind === "dialogue" && block.pose) return { kind: "character", symbol: block.pose, charId: block.speaker.toLowerCase() };
    if (block.kind === "cg" && block.name) return { kind: "cg", symbol: block.name };
    return null;
  }, [selectedStep]);
  const currentAsset = useMemo(() => {
    if (!assetTarget || !assetMapping) return undefined;
    if (assetTarget.kind === "background") return assetMapping.backgrounds.find((item) => item.symbol === assetTarget.symbol)?.assetId;
    if (assetTarget.kind === "cg") return assetMapping.cg.find((item) => item.symbol === assetTarget.symbol)?.assetId;
    return assetMapping.characters.find((character) => character.id === assetTarget.charId)?.looks.find((look) => look.symbol === assetTarget.symbol)?.assetId;
  }, [assetMapping, assetTarget]);
  const currentAssetUrl = useMemo(() => {
    if (!assetTarget || !assetUriMap) return undefined;
    if (assetTarget.kind === "background") return assetUriMap[`background:${assetTarget.symbol}`];
    if (assetTarget.kind === "cg") return assetUriMap[`cg:${assetTarget.symbol}`];
    return assetUriMap[`character:${assetTarget.charId}:${assetTarget.symbol}`];
  }, [assetTarget, assetUriMap]);
  const referenceAssetId = useMemo(() => {
    if (assetTarget?.kind !== "character" || !assetMapping) return undefined;
    if (currentAsset) return currentAsset;
    return assetMapping.characters.find((character) => character.id === assetTarget.charId)?.looks.find((look) => look.assetId)?.assetId;
  }, [assetMapping, assetTarget, currentAsset]);
  const errorLines = useMemo(() => new Set(
    (validation?.diagnostics ?? [])
      .map((diagnostic) => closestStepLine(steps, diagnostic, selectedLine))
      .filter((line): line is number => line != null),
  ), [selectedLine, steps, validation?.diagnostics]);
  const moveSelection = useCallback((delta: -1 | 1) => {
    if (steps.length === 0) return;
    const visibleLine = visibleTimelineLine(steps, selectedLine);
    const current = Math.max(0, steps.findIndex((step) => step.line === visibleLine));
    const next = Math.min(steps.length - 1, Math.max(0, current + delta));
    setSelectedLine(steps[next]!.line);
  }, [selectedLine, steps]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (isTextEditingTarget(event.target)) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      moveSelection(event.key === "ArrowLeft" ? -1 : 1);
    }
  }, [moveSelection]);

  const handlePatchStep = useCallback((line: number, fields: PresentationStepFields) => {
    if (readOnly) return;
    const step = allSteps.find((candidate) => candidate.line === line);
    if (!step) return;
    const next = patchPresentationStep(value, step, fields);
    if (next !== value) onChange(next);
  }, [allSteps, onChange, readOnly, value]);

  const handlePatch = useCallback((fields: PresentationStepFields) => {
    if (!selectedStep) return;
    handlePatchStep(selectedStep.line, fields);
  }, [handlePatchStep, selectedStep]);

  const handleAdd = useCallback((kind: InsertKind, afterLine?: number | null) => {
    if (readOnly) return;
    const result = insertPresentationTemplate(value, kind, afterLine === undefined ? selectedLine : afterLine);
    if (result.source === value) return;
    onChange(result.source);
    if (result.insertedLine != null) setSelectedLine(result.insertedLine);
  }, [onChange, readOnly, selectedLine, value]);

  const handleAddCompanion = useCallback((kind: InsertKind) => {
    if (readOnly || !selectedStep) return;
    const result = insertPresentationCompanion(value, kind, selectedStep.line);
    if (result.source === value || result.insertedLine == null) return;
    onChange(result.source);
    setSelectedLine(result.insertedLine);
  }, [onChange, readOnly, selectedStep, value]);

  const handleMove = useCallback((fromLine: number, toLine: number) => {
    if (readOnly) return;
    const result = movePresentationFrame(value, fromLine, toLine, fromLine < toLine ? "after" : "before");
    if (!result.changed) return;
    onChange(result.source);
    setSelectedLine(result.movedLine);
  }, [onChange, readOnly, value]);

  const handleDelete = useCallback((line: number) => {
    if (readOnly) return;
    const result = deletePresentationFrame(value, line);
    if (!result.changed) return;
    const index = steps.findIndex((step) => step.line === line);
    const nextFrames = buildStoryboardFrames(buildPresentationSteps(result.source));
    onChange(result.source);
    setSelectedLine(nextFrames[index]?.line ?? nextFrames[index - 1]?.line ?? null);
  }, [onChange, readOnly, steps, value]);

  const handleReplace = useCallback((kind: InsertKind) => {
    if (!selectedStep || readOnly) return;
    const result = replacePresentationStepTemplate(value, selectedStep, kind);
    if (!result.changed) return;
    onChange(result.source);
    setSelectedLine(result.insertedLine);
  }, [onChange, readOnly, selectedStep, value]);

  const handleConcurrentChange = useCallback((concurrent: boolean) => {
    if (!selectedStep || readOnly) return;
    const next = setPresentationStepConcurrent(value, selectedStep, concurrent);
    if (next !== value) onChange(next);
  }, [onChange, readOnly, selectedStep, value]);

  const canMakeConcurrent = useMemo(() => {
    if (!selectedStep || selectedStep.concurrent) return true;
    const index = allSteps.findIndex((step) => step.line === selectedStep.line);
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      const candidate = allSteps[cursor]!;
      if (candidate.depth > selectedStep.depth) continue;
      if (candidate.depth < selectedStep.depth) return false;
      if (candidate.concurrent && isStoryboardKind(candidate.kind)) continue;
      return !candidate.concurrent && isStoryboardKind(candidate.kind);
    }
    return false;
  }, [allSteps, selectedStep]);

  const handleAddChoiceOption = useCallback(() => {
    if (readOnly || !selectedStep || selectedStep.kind !== "choice") return;
    const result = addPresentationChoiceOption(value, selectedStep.line);
    if (result.source !== value) onChange(result.source);
  }, [onChange, readOnly, selectedStep, value]);

  const handleDeleteChoiceOption = useCallback((line: number) => {
    if (readOnly) return;
    const result = deletePresentationChoiceOption(value, line);
    if (result.changed) onChange(result.source);
  }, [onChange, readOnly, value]);

  const directChoiceBranch = branches.find((branch) => (
    branch.choiceLine === selectedLine || branch.options.some((option) => option.line === selectedLine)
  ));
  const selectedExitBranch = branches.find((branch) => branch.exits.some((exit) => exit.line === selectedLine));
  const choiceBranch = directChoiceBranch
    ?? branches.find((branch) => branch.choiceLine === choiceContextLine && branch.exits.some((exit) => exit.line === selectedLine))
    ?? selectedExitBranch;
  const contextChoiceLine = choiceBranch?.choiceLine;
  const handleSelectStep = useCallback((line: number) => {
    const branch = branches.find((candidate) => candidate.choiceLine === line || candidate.options.some((option) => option.line === line));
    if (branch) setChoiceContextLine(branch.choiceLine);
    else if (branches.some((candidate) => candidate.exits.some((exit) => exit.line === line))) {
      setChoiceContextLine((current) => directChoiceBranch?.choiceLine ?? current);
    } else setChoiceContextLine(null);
    setSelectedLine(line);
  }, [branches, directChoiceBranch]);
  const timelineSelectedLine = contextChoiceLine ?? visibleTimelineLine(steps, selectedLine);
  const selectedIndex = Math.max(0, steps.findIndex((step) => step.line === timelineSelectedLine));
  const previewLine = storyboardPreviewLine(allSteps, timelineSelectedLine);
  const activeFrame = steps[selectedIndex];
  const stageClickAdvance = !activeFrame || !["choice", "phone", "trick", "minigame"].includes(activeFrame.kind);
  const advance = useCallback(() => {
    if (steps[selectedIndex + 1]) setSelectedLine(steps[selectedIndex + 1]!.line);
  }, [selectedIndex, steps]);

  return (
    <main className="studio-view" onKeyDown={handleKeyDown} data-compact-panel={compactPanel}>
      <SegmentedControl<CompactPanel>
        className="studio-view__mobile-switch"
        aria-label={tr(S.panel)}
        value={compactPanel}
        onValueChange={setCompactPanel}
        options={[
          { value: "preview", label: tr(S.preview), icon: <Eye size={15} /> },
          { value: "edit", label: tr(S.edit), icon: <PanelRight size={15} /> },
        ]}
      />

      <div className="studio-grid">
        <section className="studio-panel studio-panel--preview" data-studio-panel="preview">
          <CocosStepPreview
            source={value}
            selectedLine={previewLine}
            assets={assets}
            assetUriMap={assetUriMap}
            protagonist={protagonist}
            validation={validation}
            onOpenAssets={onOpenAssets}
            onOpenPlaytest={onOpenPlaytest}
            onAdvance={stageClickAdvance ? advance : undefined}
            canAdvance={stageClickAdvance && selectedIndex < steps.length - 1}
            framePosition={{ current: steps.length ? selectedIndex + 1 : 0, total: steps.length }}
          />
        </section>

        <section className="studio-panel studio-panel--inspector" data-studio-panel="inspector">
          <StepInspector
            step={selectedStep}
            onPatch={handlePatch}
            onPatchStep={handlePatchStep}
            onOpenCode={onOpenCode}
            onOpenAssets={onOpenAssets}
            onOpenPlaytest={onOpenPlaytest}
            onReplace={handleReplace}
            onConcurrentChange={handleConcurrentChange}
            onAddCompanion={handleAddCompanion}
            choiceBranch={choiceBranch}
            contextChoiceLine={contextChoiceLine}
            optionContents={optionContents}
            gateContents={gateContents}
            companions={frameCompanions}
            canMakeConcurrent={canMakeConcurrent}
            onSelectStep={handleSelectStep}
            onAddChoiceOption={selectedStep?.kind === "choice" ? handleAddChoiceOption : undefined}
            onDeleteChoiceOption={handleDeleteChoiceOption}
            assetAssistant={assetTarget && onGenerateAsset ? (
              <StudioAssetAssistant
                target={assetTarget}
                styles={styles}
                enabled={imageGenerationEnabled}
                busy={assetsBusy}
                onGenerate={onGenerateAsset}
                onSuggest={onSuggestAssetPrompt}
                onPreviewPrompt={onPreviewAssetPrompt}
                currentAssetId={currentAsset}
                referenceAssetId={referenceAssetId}
                currentAssetUrl={currentAssetUrl}
                cutoutEnabled={cutoutEnabled}
                onCutout={assetTarget.kind === "character" && assetTarget.charId && currentAsset && onCutoutAsset ? () => onCutoutAsset({ assetId: currentAsset, symbol: assetTarget.symbol, charId: assetTarget.charId! }) : undefined}
              />
            ) : null}
            disabled={readOnly}
          />
        </section>
        <section className="studio-panel studio-panel--timeline" data-studio-panel="timeline">
          <StepFilmstrip
            steps={steps}
            sections={sections}
            branches={branches}
            tools={timelineTools}
            selectedLine={timelineSelectedLine}
            activeSourceLine={selectedLine}
            onSelect={handleSelectStep}
            onAdd={handleAdd}
            onMove={handleMove}
            onDelete={handleDelete}
            errorLines={errorLines}
            disabled={readOnly}
          />
        </section>
      </div>
    </main>
  );
}
